import api from './api'
import type {
  Profile,
  ProfileInput,
  WorkExperience,
  WorkExperienceInput,
  Education,
  EducationInput,
  Project,
  ProjectInput,
} from '../types'

export type { Profile, WorkExperience, Education, Project }

export const profileService = {
  // Profile
  async getProfileByUsername(
    username: string
  ): Promise<{ profile: Profile | null; error?: string }> {
    const response = await api.get(`/profiles/${username}`)
    if (response.data.error) {
      return { profile: null, error: response.data.error }
    }
    return { profile: response.data.profile }
  },

  async checkUsernameAvailability(username: string): Promise<{ available: boolean }> {
    const response = await api.get(`/profiles/check_username`, {
      params: { username },
    })
    return response.data
  },

  async getMyProfile(): Promise<Profile | null> {
    const response = await api.get('/profile')
    return response.data.profile
  },

  async createEmptyProfile(): Promise<Profile> {
    const response = await api.post('/profile')
    return response.data.profile
  },

  async updateProfile(data: ProfileInput): Promise<Profile> {
    const response = await api.patch('/profile', { profile: data })
    return response.data.profile
  },

  async uploadProfileImage(file: File): Promise<Profile> {
    const formData = new FormData()
    formData.append('image', file)
    const response = await api.post('/profile/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.profile
  },

  async removeProfileImage(): Promise<Profile> {
    const response = await api.delete('/profile/image')
    return response.data.profile
  },

  async uploadResume(file: File): Promise<Profile> {
    const formData = new FormData()
    formData.append('resume', file)
    const response = await api.post('/profile/resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.profile
  },

  async removeResume(): Promise<Profile> {
    const response = await api.delete('/profile/resume')
    return response.data.profile
  },

  async deleteProfile(): Promise<void> {
    await api.delete('/profile')
  },

  // Work Experiences
  async createWorkExperience(data: WorkExperienceInput): Promise<WorkExperience> {
    const response = await api.post('/work_experiences', {
      work_experience: data,
    })
    return response.data.work_experience
  },

  async updateWorkExperience(id: number, data: WorkExperienceInput): Promise<WorkExperience> {
    const response = await api.patch(`/work_experiences/${id}`, {
      work_experience: data,
    })
    return response.data.work_experience
  },

  async deleteWorkExperience(id: number): Promise<void> {
    await api.delete(`/work_experiences/${id}`)
  },

  // Education
  async createEducation(data: EducationInput): Promise<Education> {
    const response = await api.post('/educations', { education: data })
    return response.data.education
  },

  async updateEducation(id: number, data: EducationInput): Promise<Education> {
    const response = await api.patch(`/educations/${id}`, { education: data })
    return response.data.education
  },

  async deleteEducation(id: number): Promise<void> {
    await api.delete(`/educations/${id}`)
  },

  // Projects
  async createProject(data: ProjectInput): Promise<Project> {
    const response = await api.post('/projects', { project: data })
    return response.data.project
  },

  async updateProject(id: number, data: ProjectInput): Promise<Project> {
    const response = await api.patch(`/projects/${id}`, { project: data })
    return response.data.project
  },

  async deleteProject(id: number): Promise<void> {
    await api.delete(`/projects/${id}`)
  },
}
